import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "ScreenPacks",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filter Components",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Multi-Layer Wire Mesh Screen Packs",
      lead: "Precision spot-welded, aluminum-rimmed, and sintered multi-layer wire mesh screen packs engineered for continuous and batch polymer melt filtration, blown film, sheet extrusion, compounding, and pelletizing operations.",
      secondaryCta: {
        label: "View All Components",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Precision spot-welded multi-layer wire mesh screen pack assembly for extruder screen changers",
      },
      cardNote: "Multi-Layer Screen Changer Media",
      cardLink: {
        label: "Technical Specs",
        to: "/ScreenPacksPage#specifications",
      },
      specs: [
        { label: "Layer Stacks", value: "2 to 7 Layers" },
        { label: "Micron Range", value: "5 µm – 800 µm" },
        { label: "Edge Finish", value: "Spot-Welded / Aluminum Rim" },
        { label: "Pack Geometry", value: "Discs (15–600 mm) & Custom Shapes" },
      ],
    },
    overview: {
      eyebrow: "Engineered Screen Pack Assemblies",
      heading: "Graduated Multi-Layer Filtration for Extruder Screen Changers",
      paragraphs: [
        "In polymer extrusion and melt recycling systems, single mesh layers can quickly face surface blinding or deform under differential pressure. Multi-layer wire mesh screen packs address this by stacking graduated mesh counts in a predetermined sequence. A coarse outer support layer provides mechanical rigidity against breaker plate holes, intermediate redistribution layers balance polymer flow, and fine Dutch or plain weave meshes achieve precise particle capture.",
        "MutualFilter fabricates multi-layer screen packs from premium AISI 304, 304L, 316, and 316L stainless steel woven wire cloth. Layers are bonded using precision resistance spot-welding at defined pitch intervals, encapsulated within folded aluminum U-channel rims, or joined via diffusion sintering. This permanent structural joining prevents individual mesh layers from shifting, wrinkling, or bypassing under high melt viscosities.",
        "Available in circular disc formats from 15 mm to 600 mm diameter, as well as kidney, oval, and continuous belt configurations, our assemblies match standard hydraulic, slide-plate, rotary, and continuous piston screen changers. Each pack delivers dependable edge sealing to eliminate melt bypass around the breaker plate perimeter.",
      ],
      keyPoints: [
        "Multi-layer graduated mesh stacks from coarse protective grids down to fine filtration barriers prevent premature face blinding and prolong on-stream cycle time.",
        "Spot-welded joints, stamped aluminum-bound rims, or copper U-channels guarantee structural integrity and zero melt bypass at breaker plate margins.",
        "Precision fabrication in AISI 304, 316, and 316L stainless steel ensures high chemical resistance and thermal stability across diverse polymer resins.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Stainless steel multi-layer screen pack with aluminum rim for polymer melt extrusion",
      },
    },
    specTable: {
      heading: "Standard Multi-Layer Screen Pack Configurations",
      intro: "Representative graduated layer combinations for standard slide-plate and hydraulic screen changers. Custom layer counts and mesh combinations are manufactured to specification.",
      columns: [
        { key: "configuration", label: "Pack Type", align: "left" },
        { key: "layers", label: "Layer Stack", align: "left" },
        { key: "meshCombination", label: "Typical Mesh Sequence", align: "left" },
        { key: "micronRating", label: "Nominal Rating", align: "right" },
        { key: "rimStyle", label: "Edge Binding", align: "left" },
      ],
      rows: [
        {
          id: "sp-2l",
          configuration: "2-Layer Standard",
          layers: "2 Layers",
          meshCombination: "20 / 100 Mesh",
          micronRating: "150 µm",
          rimStyle: "Spot-Welded / Bare",
        },
        {
          id: "sp-3l-p",
          configuration: "3-Layer General Purpose",
          layers: "3 Layers",
          meshCombination: "20 / 60 / 20 Mesh",
          micronRating: "250 µm",
          rimStyle: "Spot-Welded / Aluminum Rim",
        },
        {
          id: "sp-3l-f",
          configuration: "3-Layer Fine Extrusion",
          layers: "3 Layers",
          meshCombination: "20 / 150 / 20 Mesh",
          micronRating: "100 µm",
          rimStyle: "Spot-Welded / Aluminum Rim",
        },
        {
          id: "sp-4l",
          configuration: "4-Layer Graduated",
          layers: "4 Layers",
          meshCombination: "20 / 60 / 200 / 20 Mesh",
          micronRating: "75 µm",
          rimStyle: "Aluminum U-Channel Rim",
        },
        {
          id: "sp-5l",
          configuration: "5-Layer Heavy Duty",
          layers: "5 Layers",
          meshCombination: "10 / 20 / 60 / 250 / 20 Mesh",
          micronRating: "58 µm",
          rimStyle: "Aluminum Rim / Spot-Welded",
        },
        {
          id: "sp-dutch",
          configuration: "High-Pressure Dutch Weave",
          layers: "3 to 5 Layers",
          meshCombination: "20 / 60 / 30x150 Dutch / 20 Mesh",
          micronRating: "25 µm – 45 µm",
          rimStyle: "Aluminum Rim / Copper Rim",
        },
      ],
      caption: "Note: Custom layer counts from 2 to 7 layers and diameter dimensions up to 600 mm are manufactured to drawing specifications.",
    },
    features: {
      heading: "Design Advantages & Construction Standards",
      intro: "Engineered specifically to solve wire migration, layer delamination, and melt bypass in demanding polymer filtration environments.",
      items: [
        {
          title: "Graduated Depth Stacking",
          text: "Arranging mesh counts in progressive succession distributes contaminant loading across the pack depth, preventing sudden differential pressure build-up.",
        },
        {
          title: "Rigid Edge Binding",
          text: "Precision stamped aluminum or copper U-channel rims firmly crimp all layer edges, ensuring snug breaker plate seating and zero perimeter polymer bypass.",
        },
        {
          title: "Anti-Delamination Spot Welding",
          text: "Controlled resistance spot-welding at calibrated intervals creates a unified pack structure that withstands manual handling and operational pressure fluctuations.",
        },
        {
          title: "Standard & Custom Geometries",
          text: "Available in circular discs from 15 mm to 600 mm, kidney shapes, rectangular strips, and custom profiles punched to tight dimensional tolerances.",
        },
      ],
    },
    related: {
      heading: "Related Filter Components & Fabricated Media",
      intro: "Explore complementary stainless steel wire mesh fabrication capabilities and engineered filtration components.",
      cards: [
        {
          title: "Filter Discs",
          text: "Precision single-layer and multi-layer stamped wire mesh discs for polymer extrusion, fluid processing, and instrumentation.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Stamped stainless steel wire mesh filter discs",
          },
          badge: "Discs & Coils",
          linkLabel: "View Filter Discs",
        },
        {
          title: "Filter Tubes & Cylinders",
          text: "Seam-welded cylindrical mesh elements and support cores for pipeline strainers, hydraulics, and chemical processing.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Stainless steel woven wire mesh filter tubes and cylinders",
          },
          badge: "Cylindrical Media",
          linkLabel: "View Filter Tubes",
        },
        {
          title: "Filter Cartridges",
          text: "Pleated and cylindrical stainless steel cartridges engineered for high-temperature fluid filtration and heavy contaminant holding.",
          to: "/FilterCartridgesPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Industrial stainless steel pleated and cylindrical filter cartridges",
          },
          badge: "Cartridge Elements",
          linkLabel: "View Filter Cartridges",
        },
        {
          title: "Filter Baskets & Plates",
          text: "Heavy-duty perforated basket assemblies, lined strainers, and breaker plates for industrial separation and fluid straining.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Industrial stainless steel filter baskets and strainer plates",
          },
          badge: "Baskets & Plates",
          linkLabel: "View Filter Baskets",
        },
        {
          title: "Filter Components Hub",
          text: "Comprehensive fabricated stainless steel filtration assemblies, custom components, and precision OEM wire cloth converting.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Overview of fabricated stainless steel filter components and assemblies",
          },
          badge: "Category Overview",
          linkLabel: "View All Components",
        },
      ],
    },
    cta: {
      id: "screen-packs-quote",
      eyebrow: "Custom Screen Pack Engineering",
      heading: "Request a Technical Quote for Screen Packs",
      lead: "Submit your layer sequence, disc diameter, mesh specifications, and edge style. Our engineering team prepares detailed dimensional and specification proposals.",
      secondaryLabel: "Browse Filter Components",
      secondaryTo: "/FilterComponentsPage",
      note: "Material test reports (MTR) and PMI alloy verification available upon request with your order.",
    },
  },
  zh: {
    breadcrumbKey: "ScreenPacks",
    relatedTopic: "components",
    hero: {
      categoryBadge: "过滤组件",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "多层组合丝网滤网片 (Screen Packs)",
      lead: "高精度点焊、铝包边及烧结多层不锈钢编织丝网组合网包，专为塑料吹膜、板片材挤出、共混造粒及再生塑料熔体换网器过滤系统量身定制。",
      secondaryCta: {
        label: "查看全部过滤组件",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "适用于挤出机换网器的精密多层组合不锈钢丝网滤网片",
      },
      cardNote: "多层换网器过滤介质",
      cardLink: {
        label: "技术规格",
        to: "/ScreenPacksPage#specifications",
      },
      specs: [
        { label: "组合层数", value: "2 至 7 层组合" },
        { label: "精度范围", value: "5 µm – 800 µm" },
        { label: "边缘工艺", value: "点焊固定 / 铝包边" },
        { label: "网片形态", value: "圆形 (15–600 mm) 及异形件" },
      ],
    },
    overview: {
      eyebrow: "工程级多层组合滤网包",
      heading: "针对挤出机多孔板与换网器的高精度阶梯过滤",
      paragraphs: [
        "在聚合物挤出及塑料回收造粒作业中，单层滤网往往由于表面杂质堆积过快而迅速堵塞，或在高压熔体冲击下嵌入多孔板孔洞发生塑性形变。多层组合滤网片通过科学搭配粗细不同的网目层叠，实现由粗至精的阶梯式纳污拦截：外层粗网支撑抗压、中间网层均流分散、高精度精滤层实现微米级精密截留。",
        "互拓公司（MutualFilter）选用优质 AISI 304、304L、316 及 316L 不锈钢丝网精制各类组合网包。网层之间可通过数控电阻点焊定位、铝合金 U 型边框冲压包覆或真空扩散烧结牢固结合。严谨的边缘密封与固定工艺彻底杜绝了滤网在高粘度熔体冲刷下的移位、分层或边缘侧漏。",
        "产品涵盖直径 15 mm 至 600 mm 的标准圆形滤网片，并可根据换网器结构定制肾形、椭圆、腰圆形及连续网带。严密的边缘平整度与多孔板贴合严实，消除旁路泄漏隐患，保障熔体纯净度与稳定产能。",
      ],
      keyPoints: [
        "梯级递进网孔结构：由粗支撑网到高精度拦截层逐级过渡，有效避免滤网表面结焦早堵，显著延长在线使用周期。",
        "高强度边缘密封：电阻点焊、冲压铝合金包边或紫铜边框，确保网包整体挺括，彻底消除多孔板边缘熔体旁漏。",
        "选用优质奥氏体不锈钢：采用 AISI 304、316 或 316L 材质，耐聚合物加工腐蚀与高温，在反复压力波动下性能可靠。",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "聚合物熔体挤出用铝合金包边多层组合不锈钢滤网包",
      },
    },
    specTable: {
      heading: "常用多层组合滤网片规格配置",
      intro: "适用于液压板式、柱式及滑板换网器的典型多层递进网目搭配组合。所有层数、网目及外形尺寸均可按图纸定制生产。",
      columns: [
        { key: "configuration", label: "组合型号", align: "left" },
        { key: "layers", label: "层数", align: "left" },
        { key: "meshCombination", label: "典型网目配置", align: "left" },
        { key: "micronRating", label: "名义过滤精度", align: "right" },
        { key: "rimStyle", label: "边缘固定形式", align: "left" },
      ],
      rows: [
        {
          id: "sp-2l-zh",
          configuration: "双层基础型",
          layers: "2 层",
          meshCombination: "20 / 100 目",
          micronRating: "150 µm",
          rimStyle: "电阻点焊 / 无包边",
        },
        {
          id: "sp-3l-p-zh",
          configuration: "三层通用型",
          layers: "3 层",
          meshCombination: "20 / 60 / 20 目",
          micronRating: "250 µm",
          rimStyle: "点焊固定 / 铝包边",
        },
        {
          id: "sp-3l-f-zh",
          configuration: "三层精细挤出型",
          layers: "3 层",
          meshCombination: "20 / 150 / 20 目",
          micronRating: "100 µm",
          rimStyle: "点焊固定 / 铝包边",
        },
        {
          id: "sp-4l-zh",
          configuration: "四层梯级阶梯型",
          layers: "4 层",
          meshCombination: "20 / 60 / 200 / 20 目",
          micronRating: "75 µm",
          rimStyle: "冲压铝合金 U 型边框",
        },
        {
          id: "sp-5l-zh",
          configuration: "五层重载挤出型",
          layers: "5 层",
          meshCombination: "10 / 20 / 60 / 250 / 20 目",
          micronRating: "58 µm",
          rimStyle: "铝包边 / 多点点焊",
        },
        {
          id: "sp-dutch-zh",
          configuration: "密纹高压挤出型",
          layers: "3 至 5 层",
          meshCombination: "20 / 60 / 30x150 密纹 / 20 目",
          micronRating: "25 µm – 45 µm",
          rimStyle: "铝合金或紫铜包边",
        },
      ],
      caption: "注：支持 2 至 7 层组合、直径最大至 600 mm 以及各种异形截面规格定制，公差精度符合工业要求。",
    },
    features: {
      heading: "结构特点与制造工艺优势",
      intro: "专为解决高压熔体挤出中的丝网分层移位、边缘跑漏及寿命短痛点而设计。",
      items: [
        {
          title: "梯级渐进层叠设计",
          text: "根据加工聚合物特性合理搭配粗保护网、导流网与密纹精滤层，梯度均摊截污负荷，防止压差快速上升。",
        },
        {
          title: "精密冲压铝包边",
          text: "精密 U 型铝合金边框严密收紧多层网片切口，杜绝边缘散丝与熔体旁路泄漏，便于操作工快速安装对中。",
        },
        {
          title: "牢固电阻定位点焊",
          text: "均匀分布的点焊节点使各层网片形成稳固整体，在冷态启动或压力脉冲下无分层、皱褶或相对位移。",
        },
        {
          title: "高精度尺寸与异形加工",
          text: "拥有完备的冲压与模具加工能力，圆形直径 15–600 mm，并可开模加工异形板件，切边光洁无毛刺。",
        },
      ],
    },
    related: {
      heading: "相关过滤组件与深加工产品",
      intro: "互拓同时提供全系列不锈钢编织丝网深加工件与非标工业滤元。",
      cards: [
        {
          title: "滤网片与分条卷带",
          text: "高精度单层及多层冲压不锈钢丝网滤片，广泛应用于塑料挤出、流体净化与仪器仪表设备。",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "冲压不锈钢编织丝网过滤圆片",
          },
          badge: "滤片与窄带",
          linkLabel: "查看滤网片详情",
        },
        {
          title: "过滤筒与滤管",
          text: "纵缝满焊或螺旋焊接不锈钢网筒与骨架支撑管，适用于管道过滤器、液压油箱与化工流体管线。",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "不锈钢编织丝网焊接过滤网筒与圆柱形滤管",
          },
          badge: "圆柱形滤元",
          linkLabel: "查看过滤筒详情",
        },
        {
          title: "不锈钢滤芯",
          text: "折叠式及圆柱式耐温耐腐蚀金属滤芯，过滤面积大，适用于高温高粘度流体及高杂质工况。",
          to: "/FilterCartridgesPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "工业不锈钢折叠滤芯及圆柱滤芯",
          },
          badge: "精密滤芯",
          linkLabel: "查看滤芯详情",
        },
        {
          title: "过滤篮与滤板",
          text: "重载冲孔板骨架衬网滤蓝、管道篮式滤网及多孔板组件，专为工业固液分离与粗精过滤配套。",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "工业重载不锈钢过滤篮与滤板组件",
          },
          badge: "滤蓝与多孔板",
          linkLabel: "查看过滤篮详情",
        },
        {
          title: "过滤组件产品中心",
          text: "互拓深加工过滤组件与工业丝网预制件全景，支持小批量工程打样与大批量规模配套。",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "互拓不锈钢深加工过滤组件系列全景",
          },
          badge: "组件总览",
          linkLabel: "查看全部过滤组件",
        },
      ],
    },
    cta: {
      id: "screen-packs-quote",
      eyebrow: "多层组合滤网定制配套",
      heading: "获取多层组合滤网片技术方案与报价",
      lead: "请告知您所需的层数结构、直径尺寸、网目精度及边缘封装方式，我们的工程师将为您提供详细的技术方案与商务报价。",
      secondaryLabel: "返回过滤组件产品中心",
      secondaryTo: "/FilterComponentsPage",
      note: "按订单要求可提供材质检验报告 (MTR) 与 PMI 光谱材质分析。",
    },
  },
  es: {
    breadcrumbKey: "ScreenPacks",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes Filtrantes",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Paquetes Filtrantes Multicapa (Screen Packs)",
      lead: "Paquetes de malla de alambre multicapa soldados por puntos, con borde de aluminio o sinterizados para cambiadores de malla en extrusión de polímeros, film soplado, compounding y reciclaje.",
      secondaryCta: {
        label: "Ver Todos los Componentes",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Paquetes de malla filtrante multicapa para cambiadores de malla en extrusión",
      },
      cardNote: "Medios Filtrantes para Cambiadores de Malla",
      cardLink: {
        label: "Especificaciones Técnicas",
        to: "/ScreenPacksPage#specifications",
      },
      specs: [
        { label: "Capas combinadas", value: "2 a 7 capas" },
        { label: "Rango de micras", value: "5 µm – 800 µm" },
        { label: "Acabado de borde", value: "Soldado por puntos / Borde de aluminio" },
        { label: "Geometría", value: "Discos (15–600 mm) y formas a medida" },
      ],
    },
    overview: {
      eyebrow: "Ensamblajes Multicapa de Precisión",
      heading: "Filtración Graduada en Profundidad para Cambiadores de Malla",
      paragraphs: [
        "En la extrusión de polímeros y el reciclaje termoplástico, el uso de mallas individuales suele provocar colmatación prematura de la superficie o deformación dentro de los orificios de la placa rompedora. Los paquetes multicapa solucionan este desafío combinando diferentes aberturas en una secuencia progresiva calculada.",
        "MutualFilter fabrica paquetes multicapa en aceros inoxidables AISI 304, 304L, 316 y 316L. Las capas se unen mediante soldadura por resistencia por puntos, remate con perfil en U de aluminio o sinterización por difusión, eliminando cualquier riesgo de desplazamiento bajo elevadas presiones de fusión.",
        "Disponibles en discos circulares de 15 mm a 600 mm de diámetro, así como en geometrías ovaladas o de riñón, nuestros ensamblajes garantizan un sellado perimetral estanco sin fugas de polímero alrededor de la placa rompedora.",
      ],
      keyPoints: [
        "Estratificación graduada multicapa que distribuye los contaminantes y evita el cegado prematuro de la cara aguas arriba.",
        "Bordes de aluminio prensado o soldadura por puntos que previenen el bypass de polímero en los márgenes de la placa perforada.",
        "Fabricación en AISI 304, 316 y 316L con alta resistencia química y estabilidad térmica ante fluctuaciones de presión.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Paquete filtrante multicapa de acero inoxidable con borde de aluminio para extrusión",
      },
    },
    specTable: {
      heading: "Configuraciones Habituales de Paquetes Multicapa",
      intro: "Combinaciones típicas de malla graduada para cambiadores de malla hidráulicos y deslizantes. Diseños a medida disponibles según plano.",
      columns: [
        { key: "configuration", label: "Tipo de Paquete", align: "left" },
        { key: "layers", label: "Capas", align: "left" },
        { key: "meshCombination", label: "Secuencia de Mallas", align: "left" },
        { key: "micronRating", label: "Retención Nominal", align: "right" },
        { key: "rimStyle", label: "Fijación Perimetral", align: "left" },
      ],
      rows: [
        {
          id: "sp-2l-es",
          configuration: "Estándar 2 Capas",
          layers: "2 Capas",
          meshCombination: "20 / 100 Mesh",
          micronRating: "150 µm",
          rimStyle: "Soldado por puntos / Libre",
        },
        {
          id: "sp-3l-p-es",
          configuration: "3 Capas Uso General",
          layers: "3 Capas",
          meshCombination: "20 / 60 / 20 Mesh",
          micronRating: "250 µm",
          rimStyle: "Soldado / Borde de aluminio",
        },
        {
          id: "sp-3l-f-es",
          configuration: "3 Capas Extrusión Fina",
          layers: "3 Capas",
          meshCombination: "20 / 150 / 20 Mesh",
          micronRating: "100 µm",
          rimStyle: "Soldado / Borde de aluminio",
        },
        {
          id: "sp-4l-es",
          configuration: "4 Capas Graduado",
          layers: "4 Capas",
          meshCombination: "20 / 60 / 200 / 20 Mesh",
          micronRating: "75 µm",
          rimStyle: "Borde en U de aluminio",
        },
        {
          id: "sp-5l-es",
          configuration: "5 Capas Trabajo Pesado",
          layers: "5 Capas",
          meshCombination: "10 / 20 / 60 / 250 / 20 Mesh",
          micronRating: "58 µm",
          rimStyle: "Borde de aluminio / Soldado",
        },
        {
          id: "sp-dutch-es",
          configuration: "Malla Holandesa Alta Presión",
          layers: "3 a 5 Capas",
          meshCombination: "20 / 60 / 30x150 Dutch / 20 Mesh",
          micronRating: "25 µm – 45 µm",
          rimStyle: "Borde de aluminio o cobre",
        },
      ],
      caption: "Nota: Posibilidad de fabricar combinaciones de 2 a 7 capas y diámetros de hasta 600 mm bajo especificación.",
    },
    features: {
      heading: "Ventajas de Diseño y Estándares de Unión",
      intro: "Desarrollados específicamente para evitar el desplazamiento de alambres y las fugas de polímero en procesos de extrusión exigentes.",
      items: [
        {
          title: "Estratificación Progresiva",
          text: "La distribución secuencial de las mallas reparte la carga de impurezas, impidiendo aumentos bruscos de presión diferencial.",
        },
        {
          title: "Bordes Rígidos en Perfil U",
          text: "El engaste con marco de aluminio prensado asegura un asentamiento perfecto y evita fugas en el perímetro de la placa.",
        },
        {
          title: "Puntos de Soldadura Robustos",
          text: "La soldadura eléctrica por resistencia une las mallas firmemente, evitando arrugas y separación de capas durante la operación.",
        },
        {
          title: "Formatos Estándar y Especiales",
          text: "Fabricación de discos desde 15 mm hasta 600 mm, así como formatos arriñonados u ovalados con tolerancias dimensionales rigurosas.",
        },
      ],
    },
    related: {
      heading: "Componentes Filtrantes Relacionados",
      intro: "Conozca otras capacidades de manufactura en acero inoxidable para filtración industrial.",
      cards: [
        {
          title: "Discos Filtrantes",
          text: "Discos de malla de alambre estampados para extrusión, purificación de fluidos y equipos de instrumentación.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Discos de malla de alambre estampados en acero inoxidable",
          },
          badge: "Discos y Bobinas",
          linkLabel: "Ver Discos Filtrantes",
        },
        {
          title: "Tubos y Cilindros Filtrantes",
          text: "Cilindros de malla con soldadura longitudinal para coladores de tubería, hidráulica y procesos químicos.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Tubos y cilindros filtrantes de malla de acero inoxidable",
          },
          badge: "Medios Cilíndricos",
          linkLabel: "Ver Tubos Filtrantes",
        },
        {
          title: "Cartuchos Filtrantes",
          text: "Cartuchos metálicos plisados y cilíndricos diseñados para fluidos a alta temperatura y retención prolongada.",
          to: "/FilterCartridgesPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Cartuchos filtrantes de acero inoxidable para la industria",
          },
          badge: "Elementos Cartucho",
          linkLabel: "Ver Cartuchos Filtrantes",
        },
        {
          title: "Cestas y Placas Filtrantes",
          text: "Cestas reforzadas con chapa perforada y filtros de canasta para separación sólido-líquido industrial.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Cestas y placas filtrantes de acero inoxidable para servicio pesado",
          },
          badge: "Cestas y Placas",
          linkLabel: "Ver Cestas Filtrantes",
        },
        {
          title: "Centro de Componentes",
          text: "Gama completa de componentes fabricados y servicios de corte y conformado de malla de alambre.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Visión general de componentes filtrantes de acero inoxidable",
          },
          badge: "Catálogo de Componentes",
          linkLabel: "Ver Todos los Componentes",
        },
      ],
    },
    cta: {
      id: "screen-packs-quote",
      eyebrow: "Fabricación de Paquetes Filtrantes a Medida",
      heading: "Solicite Cotización para Paquetes Multicapa",
      lead: "Indique la combinación de capas, diámetro, especificaciones de malla y tipo de borde requeridos para recibir una propuesta técnica completa.",
      secondaryLabel: "Volver a Componentes Filtrantes",
      secondaryTo: "/FilterComponentsPage",
      note: "Informes de prueba de material (MTR) y análisis PMI disponibles bajo requerimiento de pedido.",
    },
  },
  de: {
    breadcrumbKey: "ScreenPacks",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filterkomponenten",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Mehrlagen-Siebpakete (Screen Packs)",
      lead: "Punktgeschweißte, alugefasste und diffusionsgebundene Mehrlagen-Drahtgewebepakete für kontinuierliche Schmelzefiltration, Blasfolienanlagen, Plattenextrusion, Compoundierung und Kunststoffrecycling.",
      secondaryCta: {
        label: "Alle Filterkomponenten",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Mehrlagen-Drahtgewebepaket für Extruder-Siebwechsler",
      },
      cardNote: "Siebwechsler-Filtermedien",
      cardLink: {
        label: "Technische Daten",
        to: "/ScreenPacksPage#specifications",
      },
      specs: [
        { label: "Lagenzahl", value: "2 bis 7 Lagen" },
        { label: "Filterfeinheit", value: "5 µm – 800 µm" },
        { label: "Kantenfassung", value: "Punktgeschweißt / Alufassung" },
        { label: "Geometrie", value: "Rundscheiben (15–600 mm) & Sonderformen" },
      ],
    },
    overview: {
      eyebrow: "Präzise konfigurierte Siebbaugruppen",
      heading: "Kaskadierte Tiefenfiltration für Extruder-Siebwechsler",
      paragraphs: [
        "In der Kunststoffverarbeitung und Schmelzeaufbereitung führen Einzellagen schnell zu Oberflächenverblockung oder verformen sich unter Differenzdruck in den Bohrungen der Lochplatte. Mehrlagen-Siebpakete begegnen diesem Problem durch eine gezielte Abstufung unterschiedlicher Maschenweiten.",
        "MutualFilter fertigt Mehrlagen-Siebpakete aus hochwertigen Edelstählen AISI 304, 304L, 316 und 316L. Die Lagen werden durch Widerstandspunktschweißung, formschlüssige U-Profil-Aluminiumfassungen oder Vakuumsinterung dauerhaft miteinander verbunden, sodass Schmelzedrücke ohne Gewebeverschiebung aufgenommen werden.",
        "Lieferbar als Rundscheiben von 15 mm bis 600 mm Durchmesser sowie in Nieren-, Oval- und Bandformen passend für hydraulische Bolzen- und Schieber-Siebwechsler aller gängigen Bauarten.",
      ],
      keyPoints: [
        "Mehrlagig kaskadierter Aufbau von der Schutz- und Stützlage bis zum Feinfiltergewebe verhindert vorzeitigen Druckanstieg.",
        "Punktverschweißung oder Aluminium-U-Kantenfassung verhindern Schmelze-Bypass an der Lochplattenperipherie.",
        "Hochwertige Edelstahllegierungen (AISI 304, 316, 316L) gewährleisten thermische Stabilität und chemische Beständigkeit.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Alugefasstes Mehrlagen-Siebpaket aus Edelstahl für die Polymerschmelzefiltration",
      },
    },
    specTable: {
      heading: "Typische Mehrlagen-Siebpaketkonfigurationen",
      intro: "Bewährte Lagenkombinationen für Schieber- und hydraulische Siebwechsler. Kundenspezifische Lagenfolgen und Durchmesser werden nach Zeichnung gefertigt.",
      columns: [
        { key: "configuration", label: "Pakettyp", align: "left" },
        { key: "layers", label: "Lagenzahl", align: "left" },
        { key: "meshCombination", label: "Typische Maschenfolge", align: "left" },
        { key: "micronRating", label: "Nennfeinheit", align: "right" },
        { key: "rimStyle", label: "Kantenausführung", align: "left" },
      ],
      rows: [
        {
          id: "sp-2l-de",
          configuration: "2-Lagen Standard",
          layers: "2 Lagen",
          meshCombination: "20 / 100 Mesh",
          micronRating: "150 µm",
          rimStyle: "Punktgeschweißt / Ohne Fassung",
        },
        {
          id: "sp-3l-p-de",
          configuration: "3-Lagen Universell",
          layers: "3 Lagen",
          meshCombination: "20 / 60 / 20 Mesh",
          micronRating: "250 µm",
          rimStyle: "Punktgeschweißt / Alufassung",
        },
        {
          id: "sp-3l-f-de",
          configuration: "3-Lagen Feinextrusion",
          layers: "3 Lagen",
          meshCombination: "20 / 150 / 20 Mesh",
          micronRating: "100 µm",
          rimStyle: "Punktgeschweißt / Alufassung",
        },
        {
          id: "sp-4l-de",
          configuration: "4-Lagen Kaskadiert",
          layers: "4 Lagen",
          meshCombination: "20 / 60 / 200 / 20 Mesh",
          micronRating: "75 µm",
          rimStyle: "Aluminium-U-Profilfassung",
        },
        {
          id: "sp-5l-de",
          configuration: "5-Lagen Hochleistung",
          layers: "5 Lagen",
          meshCombination: "10 / 20 / 60 / 250 / 20 Mesh",
          micronRating: "58 µm",
          rimStyle: "Alufassung / Punktgeschweißt",
        },
        {
          id: "sp-dutch-de",
          configuration: "Hochdruck-Tressengewebe",
          layers: "3 bis 5 Lagen",
          meshCombination: "20 / 60 / 30x150 Tresse / 20 Mesh",
          micronRating: "25 µm – 45 µm",
          rimStyle: "Aluminium- oder Kupferfassung",
        },
      ],
      caption: "Hinweis: Lagenzahlen von 2 bis 7 Lagen und Durchmesser bis 600 mm werden exakt nach Vorgabe gefertigt.",
    },
    features: {
      heading: "Konstruktionsmerkmale und Fertigungsvorteile",
      intro: "Entwickelt zur Vermeidung von Drahtverzug, Gewebeaufplatzung und Schmelzebypässen in anspruchsvollen Extrusionsprozessen.",
      items: [
        {
          title: "Kaskadierte Tiefenfilterung",
          text: "Abgestufte Maschenkombinationen verteilen Verunreinigungen über die Paketebenen und verhindern plötzliche Differenzdruckspitzen.",
        },
        {
          title: "Stabile Aluminium-U-Fassung",
          text: "Präzisionsgeprägte Kantenfassungen umschließen alle Drahtkanten sauber und garantieren sicheren Sitz an der Lochplatte.",
        },
        {
          title: "Verzugsfreie Punktschweißung",
          text: "Präzise gesetzte Schweißpunkte halten das Paket stabil zusammen und erleichtern das schnelle Einlegen beim Siebwechsel.",
        },
        {
          title: "Große Form- und Maßvielfalt",
          text: "Rundscheiben von 15 mm bis 600 mm sowie Sonderformen für alle industriellen Schmelzefiltrationsanlagen.",
        },
      ],
    },
    related: {
      heading: "Verwandte Filterkomponenten & Konfektionsteile",
      intro: "Erfahren Sie mehr über unsere Drahtgewebe-Konfektionierung und montagefertigen Edelstahlkomponenten.",
      cards: [
        {
          title: "Filterscheiben & Ronden",
          text: "Präzise gestanzte Ein- und Mehrlagen-Drahtgeweberonden für Kunststoffverarbeitung und Fluidtechnik.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Gestanzte Drahtgewebe-Filterscheiben aus Edelstahl",
          },
          badge: "Ronden & Bänder",
          linkLabel: "Filterscheiben ansehen",
        },
        {
          title: "Filterrohre & Zylinder",
          text: "Längsnahtgeschweißte Filterzylinder und Stützkörper für Rohrleitungsfilter, Hydraulik und Chemieanlagen.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Geschweißte Edelstahl-Filterzylinder und Drahtgeweberohre",
          },
          badge: "Zylindrische Medien",
          linkLabel: "Filterrohre ansehen",
        },
        {
          title: "Filterkerzen",
          text: "Plissierte und zylindrische Edelstahlfilterkerzen für hohe Schmutzaufnahme und Hochtemperaturanwendungen.",
          to: "/FilterCartridgesPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Industrielle Edelstahlfilterkerzen plissiert und glatt",
          },
          badge: "Kerzenelemente",
          linkLabel: "Filterkerzen ansehen",
        },
        {
          title: "Filterkörbe & Lochbleche",
          text: "Robuste Drahtgewebekörbe mit Lochblechverstärkung für industrielle Grob- und Feinfiltration in Rohrleitungen.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Robuste Filterkörbe und Lochplatten aus Edelstahl",
          },
          badge: "Körbe & Platten",
          linkLabel: "Filterkörbe ansehen",
        },
        {
          title: "Filterkomponenten Übersicht",
          text: "Komplette Übersicht über gefertigte Edelstahl-Filterbaugruppen, Sonderteile und Lohnkonfektion.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Übersicht gefertigter Edelstahl-Filterkomponenten",
          },
          badge: "Kategorie-Übersicht",
          linkLabel: "Alle Komponenten ansehen",
        },
      ],
    },
    cta: {
      id: "screen-packs-quote",
      eyebrow: "Individuelle Siebpaket-Fertigung",
      heading: "Technisches Angebot für Siebpakete anfordern",
      lead: "Nennen Sie uns Ihre gewünschte Lagenfolge, Durchmesser, Gewebespezifikationen und Kantenausführung für ein schnelles Angebot.",
      secondaryLabel: "Zu den Filterkomponenten",
      secondaryTo: "/FilterComponentsPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Wunsch bei Bestellung erhältlich.",
    },
  },
  fr: {
    breadcrumbKey: "ScreenPacks",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Composants Filtrants",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Packs Filtrants Multicouches (Screen Packs)",
      lead: "Packs de toiles métalliques multicouches soudés par points, cerclés d'aluminium ou frittés pour l'extrusion de polymères, le film soufflé, le compoundage et le recyclage des matières plastiques.",
      secondaryCta: {
        label: "Tous les Composants",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Packs de toiles métalliques multicouches pour changeurs de filtres d'extrusion",
      },
      cardNote: "Milieux Filtrants pour Changeurs de Filtres",
      cardLink: {
        label: "Spécifications Techniques",
        to: "/ScreenPacksPage#specifications",
      },
      specs: [
        { label: "Empilements", value: "2 à 7 couches" },
        { label: "Seuils de rétention", value: "5 µm – 800 µm" },
        { label: "Finition des bords", value: "Soudé par points / Cerclage alu" },
        { label: "Formats", value: "Disques (15–600 mm) et formes spéciales" },
      ],
    },
    overview: {
      eyebrow: "Assemblages Multicouches de Précision",
      heading: "Filtration en Profondeur Échelonnée pour Changeurs de Filtres",
      paragraphs: [
        "Dans l'extrusion des polymères et le recyclage des thermoplastiques, une toile unique est sujette à un colmatage surfacique précoce ou à un emboutissage dans les trous de la plaque perforée. Les packs multicouches résolvent ce phénomène grâce à une succession calculée de différentes finesses de mailles.",
        "MutualFilter fabrique des packs multicouches en acier inoxydable AISI 304, 304L, 316 et 316L. Les toiles sont assemblées par soudure électrique par points, par cerclage en profil U aluminium ou par frittage sous vide, éliminant tout risque de glissement sous les pressions élevées du polymère en fusion.",
        "Disponibles sous forme de disques de 15 mm à 600 mm de diamètre ainsi qu'en formes haricot ou ovales, ces empilements garantissent une étanchéité périphérique absolue sans fuite de matière autour de la plaque brise-jet.",
      ],
      keyPoints: [
        "Graduation multicouche optimisée de la protection amont à la barrière fine évitant le colmatage précoce et allongeant les cycles de production.",
        "Soudure par points ou cerclage en U aluminium/cuivre garantissant une étanchéité absolue au bord de la grille perforée.",
        "Fabrication en acier inoxydable AISI 304, 316 et 316L pour une résistance chimique et thermique irréprochable.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Pack filtrant multicouche en acier inoxydable avec bord aluminium pour extrusion",
      },
    },
    specTable: {
      heading: "Configurations Standards de Packs Multicouches",
      intro: "Combinaisons types de toiles métalliques étagées pour changeurs de filtres hydrauliques et à tiroir. Fabrications sur mesure selon plan.",
      columns: [
        { key: "configuration", label: "Type de Pack", align: "left" },
        { key: "layers", label: "Couches", align: "left" },
        { key: "meshCombination", label: "Séquence de Toiles", align: "left" },
        { key: "micronRating", label: "Seuil Nominal", align: "right" },
        { key: "rimStyle", label: "Bordure / Assemblage", align: "left" },
      ],
      rows: [
        {
          id: "sp-2l-fr",
          configuration: "Standard 2 Couches",
          layers: "2 Couches",
          meshCombination: "20 / 100 Mesh",
          micronRating: "150 µm",
          rimStyle: "Soudé par points / Bords nus",
        },
        {
          id: "sp-3l-p-fr",
          configuration: "3 Couches Usage Général",
          layers: "3 Couches",
          meshCombination: "20 / 60 / 20 Mesh",
          micronRating: "250 µm",
          rimStyle: "Soudé / Cerclage alu",
        },
        {
          id: "sp-3l-f-fr",
          configuration: "3 Couches Extrusion Fine",
          layers: "3 Couches",
          meshCombination: "20 / 150 / 20 Mesh",
          micronRating: "100 µm",
          rimStyle: "Soudé / Cerclage alu",
        },
        {
          id: "sp-4l-fr",
          configuration: "4 Couches Échelonné",
          layers: "4 Couches",
          meshCombination: "20 / 60 / 200 / 20 Mesh",
          micronRating: "75 µm",
          rimStyle: "Cerclage profil U aluminium",
        },
        {
          id: "sp-5l-fr",
          configuration: "5 Couches Haute Pression",
          layers: "5 Couches",
          meshCombination: "10 / 20 / 60 / 250 / 20 Mesh",
          micronRating: "58 µm",
          rimStyle: "Cerclage alu / Multipoints",
        },
        {
          id: "sp-dutch-fr",
          configuration: "Toile Repoussée Haute Pression",
          layers: "3 à 5 Couches",
          meshCombination: "20 / 60 / 30x150 Dutch / 20 Mesh",
          micronRating: "25 µm – 45 µm",
          rimStyle: "Cerclage alu ou cuivre",
        },
      ],
      caption: "Note : Fabrications spéciales de 2 à 7 couches et diamètres jusqu'à 600 mm réalisables selon vos tolérances.",
    },
    features: {
      heading: "Avantages de Conception et Finitions",
      intro: "Conçus pour éliminer les déformations de fils et les fuites latérales sous forte pression différentielle.",
      items: [
        {
          title: "Empilement Échelonné",
          text: "La gradation des mailles répartit la charge d'impuretés dans l'épaisseur du pack pour éviter les montées brutales de pression.",
        },
        {
          title: "Cerclage Rigide en U",
          text: "Le profilé aluminium serti retient fermement les arêtes découpées et garantit un appui étanche contre la plaque perforée.",
        },
        {
          title: "Soudure par Résistance Homogène",
          text: "Les points de soudure calibrés maintiennent l'empilement solidaire pour une manipulation aisée et une pose sans plis.",
        },
        {
          title: "Géométries Diversifiées",
          text: "Disques de 15 mm à 600 mm, formes en haricot ou bandes rectangulaires découpés avec une grande précision dimensionnelle.",
        },
      ],
    },
    related: {
      heading: "Composants Filtrants Complémentaires",
      intro: "Découvrez nos autres fabrications industrielles en toiles métalliques inox.",
      cards: [
        {
          title: "Disques Filtrants",
          text: "Disques découpés et emboutis en toiles métalliques simples ou multicouches pour la plasturgie et les fluides.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Disques filtrants en toile métallique inox estampés",
          },
          badge: "Disques et Bandes",
          linkLabel: "Voir les Disques Filtrants",
        },
        {
          title: "Tubes et Cylindres Filtrants",
          text: "Éléments cylindriques à soudure longitudinale pour filtres de tuyauterie, hydraulique et chimie.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Tubes et cylindres filtrants en toile métallique soudée",
          },
          badge: "Milieux Cylindriques",
          linkLabel: "Voir les Tubes Filtrants",
        },
        {
          title: "Cartouches Filtrantes",
          text: "Cartouches plissées et lisses en acier inoxydable adaptées aux températures élevées et aux fluides visqueux.",
          to: "/FilterCartridgesPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Cartouches filtrantes industrielles en acier inoxydable",
          },
          badge: "Éléments Cartouches",
          linkLabel: "Voir les Cartouches Filtrantes",
        },
        {
          title: "Paniers et Plaques Filtrantes",
          text: "Paniers filtrants doublés sur tôle perforée et plaques de tamisage pour la séparation solide-liquide.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Paniers et plaques filtrantes en inox pour filtration lourde",
          },
          badge: "Paniers et Plaques",
          linkLabel: "Voir les Paniers Filtrants",
        },
        {
          title: "Gamme Composants Filtrants",
          text: "Vue d'ensemble de nos fabrications sur mesure et pièces découpées en toiles métalliques industrielles.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Aperçu des composants filtrants industriels en acier inoxydable",
          },
          badge: "Vue d'ensemble",
          linkLabel: "Tous les Composants",
        },
      ],
    },
    cta: {
      id: "screen-packs-quote",
      eyebrow: "Fabrication de Packs Filtrants sur Mesure",
      heading: "Demandez une Étude et un Devis pour Packs Multicouches",
      lead: "Transmettez-nous votre empilement de toiles, dimensions, finesses et type de bordure pour recevoir une offre technique détaillée.",
      secondaryLabel: "Consulter les Composants Filtrants",
      secondaryTo: "/FilterComponentsPage",
      note: "Certificats matière (MTR) et contrôle PMI disponibles sur demande lors de votre commande.",
    },
  },
  pt: {
    breadcrumbKey: "ScreenPacks",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes Filtrantes",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Conjuntos de Pacotes Filtrantes Multicamadas (Screen Packs)",
      lead: "Pacotes de malha metálica multicamadas soldados por pontos, com borda de alumínio ou sinterizados para extrusão contínua de polímeros, filme soprado, chapas e reciclagem.",
      secondaryCta: {
        label: "Ver Todos os Componentes",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Pacotes filtrantes multicamadas para troca-filtros de extrusão",
      },
      cardNote: "Meios Filtrantes para Troca-Filtros",
      cardLink: {
        label: "Especificações Técnicas",
        to: "/ScreenPacksPage#specifications",
      },
      specs: [
        { label: "Combinações de camadas", value: "2 a 7 camadas" },
        { label: "Faixa de micragem", value: "5 µm – 800 µm" },
        { label: "Acabamento de borda", value: "Ponto de solda / Borda de alumínio" },
        { label: "Geometria", value: "Discos (15–600 mm) e formatos especiais" },
      ],
    },
    overview: {
      eyebrow: "Conjuntos Multicamadas de Engenharia",
      heading: "Filtragem Graduada em Profundidade para Troca-Filtros de Extrusão",
      paragraphs: [
        "Em linhas de extrusão de polímeros e reciclagem de termoplásticos, o uso de telas simples provoca colmatação prematura ou empenamento nos furos da placa de quebra de fluxo. Os pacotes multicamadas superam essa limitação combinando aberturas graduadas numa sequência calculada.",
        "A MutualFilter fabrica pacotes multicamadas em aços inoxidáveis AISI 304, 304L, 316 e 316L. A união das camadas é realizada por solda a ponto por resistência, bordas prensadas em perfil U de alumínio ou sinterização por difusão, resistindo a picos de pressão sem deslocamento das telas.",
        "Disponíveis em discos circulares de 15 mm a 600 mm de diâmetro, bem como em geometrias especiais (formato rim, oblongos e tiras contínuas), nossos pacotes garantem vedação firme sem vazamento perimétrico de polímero.",
      ],
      keyPoints: [
        "Gradação multicamadas desde a proteção grosseira até retenção fina para evitar o entupimento prematuro e ampliar a vida útil.",
        "Fixação por ponto de solda ou bordas em U de alumínio/cobre garantindo estanqueidade total na placa perfurada.",
        "Construção em AISI 304, 316 e 316L para elevada resistência química e estabilidade térmica sob fluxo contínuo de polímeros.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
        alt: "Pacote filtrante multicamadas em aço inoxidável com borda de alumínio para extrusão",
      },
    },
    specTable: {
      heading: "Configurações Típicas de Pacotes Multicamadas",
      intro: "Combinações de malhas comumente aplicadas em troca-filtros hidráulicos e tipo gaveta. Projetos especiais conforme desenho técnico.",
      columns: [
        { key: "configuration", label: "Tipo de Pacote", align: "left" },
        { key: "layers", label: "Camadas", align: "left" },
        { key: "meshCombination", label: "Sequência de Malhas", align: "left" },
        { key: "micronRating", label: "Micragem Nominal", align: "right" },
        { key: "rimStyle", label: "Fixação Periférica", align: "left" },
      ],
      rows: [
        {
          id: "sp-2l-pt",
          configuration: "Padrão 2 Camadas",
          layers: "2 Camadas",
          meshCombination: "20 / 100 Mesh",
          micronRating: "150 µm",
          rimStyle: "Ponto de solda / Sem borda",
        },
        {
          id: "sp-3l-p-pt",
          configuration: "3 Camadas Uso Geral",
          layers: "3 Camadas",
          meshCombination: "20 / 60 / 20 Mesh",
          micronRating: "250 µm",
          rimStyle: "Soldado / Borda de alumínio",
        },
        {
          id: "sp-3l-f-pt",
          configuration: "3 Camadas Extrusão Fina",
          layers: "3 Camadas",
          meshCombination: "20 / 150 / 20 Mesh",
          micronRating: "100 µm",
          rimStyle: "Soldado / Borda de alumínio",
        },
        {
          id: "sp-4l-pt",
          configuration: "4 Camadas Graduado",
          layers: "4 Camadas",
          meshCombination: "20 / 60 / 200 / 20 Mesh",
          micronRating: "75 µm",
          rimStyle: "Borda em U de alumínio",
        },
        {
          id: "sp-5l-pt",
          configuration: "5 Camadas Serviço Pesado",
          layers: "5 Camadas",
          meshCombination: "10 / 20 / 60 / 250 / 20 Mesh",
          micronRating: "58 µm",
          rimStyle: "Borda de alumínio / Ponto de solda",
        },
        {
          id: "sp-dutch-pt",
          configuration: "Trama Holandesa Alta Pressão",
          layers: "3 a 5 Camadas",
          meshCombination: "20 / 60 / 30x150 Dutch / 20 Mesh",
          micronRating: "25 µm – 45 µm",
          rimStyle: "Borda de alumínio ou cobre",
        },
      ],
      caption: "Nota: Fabricação personalizada de 2 a 7 camadas e diâmetros de até 600 mm conforme especificações do cliente.",
    },
    features: {
      heading: "Diferenciais Técnicos e Acabamento",
      intro: "Projetados para eliminar migração de arames e vazamentos nas bordas em condições severas de extrusão.",
      items: [
        {
          title: "Empilhamento em Profundidade",
          text: "A progressão controlada de aberturas reparte os particulados nas camadas internas, evitando incrementos rápidos de pressão.",
        },
        {
          title: "Borda Rígida em Perfil U",
          text: "O enclausuramento das bordas com perfil de alumínio impede desfiamento e assegura vedação impecável na placa perfurada.",
        },
        {
          title: "Solda a Ponto Confiável",
          text: "Pontos de resistência distribuídos mantêm a geometria uniforme, facilitando o manuseio e a substituição ágil do pacote.",
        },
        {
          title: "Variedade de Geometrias",
          text: "Fornecimento em discos de 15 mm a 600 mm, bem como formas especiais cortadas com rigorosas tolerâncias dimensionais.",
        },
      ],
    },
    related: {
      heading: "Componentes Filtrantes Relacionados",
      intro: "Conheça outras soluções em telas metálicas conformadas para filtração industrial.",
      cards: [
        {
          title: "Discos Filtrantes",
          text: "Discos de tela metálica estampados em camada simples ou múltipla para polímeros e processamento de fluidos.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Discos filtrantes de tela metálica estampados em aço inoxidável",
          },
          badge: "Discos e Bobinas",
          linkLabel: "Ver Discos Filtrantes",
        },
        {
          title: "Tubos e Cilindros Filtrantes",
          text: "Cilindros de tela com solda longitudinal para filtros de linha, sistemas hidráulicos e processamento químico.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Tubos e cilindros filtrantes de tela de aço inoxidável",
          },
          badge: "Meios Cilíndricos",
          linkLabel: "Ver Tubos Filtrantes",
        },
        {
          title: "Cartuchos Filtrantes",
          text: "Cartuchos metálicos plissados e cilíndricos desenvolvidos para fluidos em alta temperatura e retenção severa.",
          to: "/FilterCartridgesPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Cartuchos filtrantes industriais de aço inoxidável",
          },
          badge: "Elementos Cartucho",
          linkLabel: "Ver Cartuchos Filtrantes",
        },
        {
          title: "Cestos e Placas Filtrantes",
          text: "Cestos reforçados com chapa perfurada e placas de retenção para separação industrial sólido-líquido.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Cestos e placas filtrantes reforçadas em aço inoxidável",
          },
          badge: "Cestos e Placas",
          linkLabel: "Ver Cestos Filtrantes",
        },
        {
          title: "Hub de Componentes Filtrantes",
          text: "Linha completa de componentes fabricados, peças especiais e serviços de corte de telas metálicas.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Visão geral de componentes filtrantes industriais em aço inoxidável",
          },
          badge: "Visão Geral",
          linkLabel: "Ver Todos os Componentes",
        },
      ],
    },
    cta: {
      id: "screen-packs-quote",
      eyebrow: "Fabricação Sob Medida de Pacotes Filtrantes",
      heading: "Solicite uma Cotação Técnica para Pacotes Multicamadas",
      lead: "Envie sua sequência de camadas, diâmetro dos discos, especificações de malha e modelo de borda para receber uma proposta completa.",
      secondaryLabel: "Ver Componentes Filtrantes",
      secondaryTo: "/FilterComponentsPage",
      note: "Relatórios de teste de material (MTR) e verificação de liga PMI disponíveis mediante solicitação no pedido.",
    },
  },
};
